import { env } from '@/lib/env'

/**
 * Where the JWT pair lives. uc-core issues { accessToken, refreshToken, expiresIn } and rotates the
 * refresh token on every POST /auth/refresh. Default storage is localStorage because a phone must
 * keep syncing after the app restarts; VITE_AUTH_STORAGE=session narrows it to the tab.
 */
export interface TokenPair {
  accessToken: string
  refreshToken: string
  /** Epoch ms when the access token expires (from expiresIn at issue time). */
  expiresAt: number
}

const KEY = 'ucsc.auth.tokens.v1'
type Listener = (tokens: TokenPair | null) => void

function storage(): Storage | null {
  try {
    return env.authStorage === 'session' ? sessionStorage : localStorage
  } catch {
    return null
  }
}

function read(): TokenPair | null {
  try {
    const raw = storage()?.getItem(KEY)
    if (!raw) return null
    const v = JSON.parse(raw) as Partial<TokenPair>
    if (typeof v.accessToken !== 'string' || typeof v.refreshToken !== 'string') return null
    return {
      accessToken: v.accessToken,
      refreshToken: v.refreshToken,
      expiresAt: Number(v.expiresAt) || 0,
    }
  } catch {
    return null
  }
}

let current: TokenPair | null = read()
const listeners = new Set<Listener>()

export const tokenStore = {
  get(): TokenPair | null {
    return current
  },
  set(tokens: TokenPair | null): void {
    current = tokens
    try {
      if (tokens) storage()?.setItem(KEY, JSON.stringify(tokens))
      else storage()?.removeItem(KEY)
    } catch {
      /* storage full or blocked — keep in memory */
    }
    listeners.forEach((l) => l(tokens))
  },
  clear(): void {
    tokenStore.set(null)
  },
  subscribe(listener: Listener): () => void {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },
}

/** Keep tabs in step: signing out in one tab signs out the others. */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== KEY) return
    current = read()
    listeners.forEach((l) => l(current))
  })
}
